import React, {FormEvent} from "react";
import {Text, styled, theme} from "@reside/ui";
import {TableUserDto} from "../../../../utils/types/user";
import {
  BulkAssignmentVariant,
  formatBulkCategoryBadgeLabel,
  getBulkAssignmentState,
  getBulkCategoryBadgeVariant,
} from "./bulkPermissions.helpers";
import {
  BulkPermissionCategoryCard,
  BulkPermissionCategoryItem,
  BulkPermissionRow,
} from "./BulkPermissionCategoryCard";

type Props = Readonly<{
  users: ReadonlyArray<TableUserDto>;
  dashboardOptions: ReadonlyArray<{id?: string; name?: string}>;
  isLoading: boolean;
  isError?: boolean;
  isSaving: boolean;
  hasDashboard: (userId: string, dashboardId: string) => boolean;
  isDashboardPending: (dashboardId: string) => boolean;
  onToggleDashboard: (dashboardId: string, grantToAll: boolean) => void;
  onToggleAllDashboards: (grantToAll: boolean) => void;
}>;

export const BulkQuicksightDashboardsCheckbox = ({
  users,
  dashboardOptions,
  isLoading,
  isError = false,
  isSaving,
  hasDashboard,
  isDashboardPending,
  onToggleDashboard,
  onToggleAllDashboards,
}: Props) => {
  const userIds = users
    .filter(
      (user): user is TableUserDto & {id: string; email: string} =>
        Boolean(user.id && user.email),
    )
    .map(user => user.id);
  const dashboardIds = dashboardOptions
    .map(dashboard => dashboard.id)
    .filter((id): id is string => Boolean(id));
  const dashboardStates = dashboardIds.map(dashboardId =>
    getBulkAssignmentState(userIds, userId =>
      hasDashboard(userId, dashboardId),
    ),
  );
  const someChecked = dashboardStates.some(state => state.assignedCount > 0);
  const everyChecked =
    dashboardIds.length > 0 &&
    dashboardStates.every(state => state.variant === BulkAssignmentVariant.All);
  const categoryPending = dashboardIds.some(dashboardId =>
    isDashboardPending(dashboardId),
  );
  const disabled = isLoading || isSaving || isError;

  return (
    <BulkPermissionCategoryCard
      label="Quicksight Dashboards"
      badgeLabel={formatBulkCategoryBadgeLabel(dashboardStates)}
      badgeVariant={getBulkCategoryBadgeVariant(dashboardStates)}
      someChecked={someChecked}
      everyChecked={everyChecked}
      isPending={categoryPending}
      disabled={disabled}
      onGroupChange={(event: FormEvent<HTMLInputElement>) => {
        if (disabled || dashboardIds.length === 0) {
          return;
        }

        onToggleAllDashboards(event.currentTarget.checked);
      }}
    >
      {isLoading ? (
        <LoadingState>Loading...</LoadingState>
      ) : isError ? (
        <LoadingState>
          Couldn&apos;t load dashboards for some users. Try again from the user
          list.
        </LoadingState>
      ) : dashboardIds.length === 0 ? (
        <LoadingState>No dashboards available.</LoadingState>
      ) : (
        dashboardOptions.map(({id: dashboardId, name}) =>
          dashboardId ? (
            <BulkPermissionCategoryItem key={dashboardId}>
              <BulkPermissionRow
                label={name}
                userIds={userIds}
                hasAssignment={userId => hasDashboard(userId, dashboardId)}
                isPending={isDashboardPending(dashboardId)}
                disabled={disabled}
                onChange={(event: FormEvent<HTMLInputElement>) => {
                  if (disabled) {
                    return;
                  }

                  onToggleDashboard(dashboardId, event.currentTarget.checked);
                }}
              />
            </BulkPermissionCategoryItem>
          ) : null,
        )
      )}
    </BulkPermissionCategoryCard>
  );
};

const LoadingState = styled(Text)`
  color: ${theme.color.gray150};
  display: block;
  font-size: 14px;
  line-height: 20px;
  padding: 16px;
`;
